import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Pause, Play } from "lucide-react";

const CURRENTS = [
    {
        name: "White",
        tint: "text-silver",
        ring: "hover:border-silver/50 hover:shadow-[0_0_40px_rgba(226,232,240,0.18)]",
        text: "Healing, protection, cleansing and attraction.",
    },
    {
        name: "Grey",
        tint: "text-[#e0aaff]",
        ring: "hover:border-[#e0aaff]/50 hover:shadow-[0_0_40px_rgba(224,170,255,0.22)]",
        text: "Restoring balance, revealing hidden influences, redirecting displaced energy or fate.",
    },
    {
        name: "Black",
        tint: "text-[#c77dff]",
        ring: "hover:border-[#c77dff]/60 hover:shadow-[0_0_40px_rgba(123,44,191,0.4)]",
        text: "Reserved for binding harmful forces, breaking spiritual interference, reversing malicious work and returning energy to its source.",
    },
];

export const MeetMara = ({ onBegin }) => {
    const audioRef = useRef(null);
    const [voicePlaying, setVoicePlaying] = useState(false);

    const toggleVoice = () => {
        const el = audioRef.current;
        if (!el) return;
        if (voicePlaying) {
            el.pause();
        } else {
            el.play().catch(() => setVoicePlaying(false));
        }
    };

    return (
        <section id="mara" data-testid="mara-section" className="relative px-5 py-28 sm:px-8 lg:py-36">
            <div className="nebula-blob left-[-8%] top-[15%] h-[28rem] w-[28rem] bg-[#5a189a]/25" />
            <div className="nebula-blob bottom-[5%] right-[15%] h-80 w-80 bg-[#2e0b53]/40" />

            <div className="mx-auto max-w-6xl">
                <div className="grid items-center gap-14 lg:grid-cols-[2fr_3fr]">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1.1 }}
                        className="relative mx-auto w-full max-w-sm"
                    >
                        <div className="portal-ring absolute -inset-5 animate-spin-slower rounded-t-[999px] rounded-b-3xl" />
                        <div className="absolute -inset-3 animate-pulse-soft rounded-t-[999px] rounded-b-3xl bg-[#7b2cbf]/25 blur-2xl" />
                        <div className="relative aspect-[3/4] overflow-hidden rounded-t-[999px] rounded-b-3xl ring-1 ring-silver/30 shadow-[0_0_70px_rgba(123,44,191,0.5)]">
                            <img
                                src="/assets/mara-photo.jpg"
                                alt="Mara Vey — practitioner of the three currents"
                                data-testid="mara-portrait"
                                className="h-full w-full object-cover object-top"
                            />
                            <div className="mirror-sheen absolute inset-0" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#06010f]/70 via-transparent to-transparent" />
                        </div>
                        <span className="absolute -right-2 top-16 h-2 w-2 rounded-full bg-[#ffd700]/70 shadow-[0_0_12px_rgba(255,215,0,0.6)]" />
                        <span className="absolute -left-1 bottom-20 h-1.5 w-1.5 rounded-full bg-[#ffd700]/50 shadow-[0_0_10px_rgba(255,215,0,0.5)]" />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                    >
                        <p className="eyebrow mb-4">Chapter II · Meet Mara Vey</p>
                        <h2 className="text-glow-lilac font-display text-2xl font-bold leading-snug tracking-wide text-[#f8f7ff] sm:text-3xl lg:text-4xl">
                            THE SEEKER OF HIDDEN TRUTHS
                        </h2>
                        <div className="mt-6 flex items-center gap-4">
                            <button
                                type="button"
                                onClick={toggleVoice}
                                data-testid="mara-voice-toggle"
                                className="btn-ghost-glow inline-flex items-center gap-3 !px-5 !py-2.5 !text-[10px]"
                            >
                                {voicePlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                                {voicePlaying ? "PAUSE MARA'S VOICE" : "HEAR MARA'S VOICE"}
                            </button>
                            {voicePlaying && (
                                <span className="flex items-end gap-[3px]" data-testid="mara-voice-playing-indicator">
                                    {[0, 1, 2, 3].map((i) => (
                                        <span
                                            key={i}
                                            className="voice-bar w-[3px] rounded-full bg-[#c77dff]"
                                            style={{ animationDelay: `${i * 0.15}s` }}
                                        />
                                    ))}
                                </span>
                            )}
                        </div>
                        <p
                            data-testid="mara-manifesto"
                            className="mt-5 max-w-md border-l-2 border-[#c77dff]/40 pl-5 font-cormorant text-base italic leading-relaxed text-[#e0aaff]/80"
                        >
                            "Energy has no fixed shape. It moves where it's directed. So stop calling fear fate and
                            hesitation divine timing. If you want something, move the energy toward it. Take it. Shape
                            it. Claim it. Magic was never made for people waiting for permission. Rules, after all,
                            were made to be broken."
                        </p>
                        <audio
                            ref={audioRef}
                            src="/assets/mara-greeting.mp3"
                            preload="none"
                            data-testid="mara-voice-audio"
                            onPlay={() => setVoicePlaying(true)}
                            onPause={() => setVoicePlaying(false)}
                            onEnded={() => setVoicePlaying(false)}
                        />
                        <div className="mt-7 space-y-5 font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/85">
                            <p>
                                Mara Vey doesn't look at the world through the lens of ordinary rules, labels, or public
                                opinions. For over twenty years, she has operated quietly in the shadows as a trusted
                                confidante to those facing the most complicated, intensely private, and misunderstood
                                situations in life.
                            </p>
                            <p>
                                Specializing in ancient bone reading, deep crystal channeling, and raw energetic
                                restructuring, Mara provides an absolute sanctuary for your secrets. Where others bring
                                judgment, finger-pointing, and rigid morality, Mara brings complete silence and immense
                                clarity.
                            </p>
                        </div>
                        <blockquote className="mt-8 border-l-2 border-[#c77dff]/50 pl-6">
                            <p className="font-cormorant text-xl italic leading-relaxed text-[#f8f7ff]/90">
                                "I do not care about what society labels as 'right' or 'wrong.' I care about alignment. I
                                care about who has the strength to claim the path the universe left open for them. If
                                something belongs to you energetically, it is your right to anchor it into reality."
                            </p>
                            <footer className="mt-3 font-cinzel text-xs tracking-[0.3em] text-[#c77dff]/70">— MARA</footer>
                        </blockquote>
                        <p className="mt-8 font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/85">
                            Whether you are trying to secure a deep, unspoken bond, shield your home from a bitter
                            person's malicious attacks, or claim a life that feels destined to be yours, Mara reads the
                            currents without bias. She does not change the truth to make people comfortable; she simply
                            hands you the keys to your own power.
                        </p>
                        <button type="button" onClick={onBegin} data-testid="mara-speak-button" className="btn-glow mt-10 inline-flex items-center gap-3">
                            <MessageCircle className="relative h-4 w-4" />
                            <span className="mirror-sheen absolute inset-0 animate-shimmer-line" />
                            <span className="relative">SPEAK TO MARA</span>
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mt-28"
                    data-testid="three-currents"
                >
                    <p className="eyebrow mb-4">Her Practice</p>
                    <h3 className="text-glow-lilac font-display text-xl font-bold tracking-wide text-[#f8f7ff] sm:text-2xl lg:text-3xl">
                        PRACTITIONER OF THE THREE CURRENTS
                    </h3>
                    <p className="mt-3 font-cormorant text-xl italic text-[#e0aaff]/80">White. Grey. Black.</p>
                    <div className="mt-7 max-w-3xl space-y-5 font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/85">
                        <p>
                            Mara works across the complete spectrum of spiritual practice. She is not restricted to love and
                            light, nor does she enter darker workings without purpose. Every situation is read according to
                            its true nature, and the current required is chosen accordingly.
                        </p>
                        <p>
                            Her work reaches beneath what is visible to uncover where a path was altered, where energy became
                            attached, and where something destined for you found its way into another life. Once identified,
                            she works to sever the interference, reclaim what belongs to you and return your fate to its
                            rightful path.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {CURRENTS.map((c, i) => (
                            <motion.button
                                key={c.name}
                                type="button"
                                onClick={onBegin}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.8, delay: i * 0.15 }}
                                data-testid={`current-${c.name.toLowerCase()}`}
                                className={`glass-card glass-card-hover relative cursor-pointer overflow-hidden p-7 text-left ${c.ring}`}
                            >
                                <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-25" />
                                <p className={`font-display text-2xl font-bold tracking-wide ${c.tint}`}>{c.name}</p>
                                <p className="mt-4 font-cormorant text-base italic leading-relaxed text-[#f8f7ff]/80">
                                    {c.text}
                                </p>
                                <p className="mt-5 font-cinzel text-[10px] tracking-[0.25em] text-[#c77dff]/70">
                                    BEGIN WITH THIS CURRENT →
                                </p>
                            </motion.button>
                        ))}
                    </div>

                    <p className="mt-12 text-center font-cormorant text-xl italic text-[#f8f7ff]/85">
                        There is no judgement here — only truth, intention and consequence.
                    </p>
                </motion.div>
            </div>
        </section>
    );
};
